import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { MeasureText } from '../geometry/drawable';
import { cursorForHandle, pickElement, pickHandle } from '../geometry/hit';
import type { Vec } from '../geometry/transform';
import { renderScene } from '../render/canvas';
import { renderOverlay } from '../render/overlay';
import type { Action, Tool } from '../scene/reducer';
import type { BoardView, SlateElement } from '../scene/types';
import { screenToWorld } from '../scene/view';
import type { Rect } from '../geometry/transform';
import type { Peer } from '../collab/presence';

interface Props {
  readonly elements: readonly SlateElement[];
  readonly selectedIds: readonly string[];
  readonly view: BoardView;
  readonly tool: Tool;
  readonly marquee: Rect | null;
  readonly hoverBindTargetId: string | null;
  readonly peers: readonly Peer[];
  readonly measure: MeasureText;
  readonly interacting: boolean;
  /** Space held, or middle button: pan without disturbing tool or selection. */
  readonly panOverride: boolean;
  readonly dispatch: (action: Action) => void;
  readonly onPointerWorld: (world: Vec) => void;
  readonly onViewportChange: (size: { width: number; height: number }) => void;
}

const GRID_SPACING = 24;

function cssVar(node: HTMLElement, name: string, fallback: string): string {
  const value = getComputedStyle(node).getPropertyValue(name).trim();
  return value.length > 0 ? value : fallback;
}

export function Canvas(props: Props) {
  const {
    elements,
    selectedIds,
    view,
    tool,
    marquee,
    hoverBindTargetId,
    peers,
    measure,
    interacting,
    panOverride,
    dispatch,
    onPointerWorld,
    onViewportChange,
  } = props;

  const hostRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sizeRef = useRef({ width: 0, height: 0, pixelRatio: 1 });
  const frameRef = useRef<number | null>(null);
  const localPanRef = useRef<{ startClient: Vec; startScroll: Vec } | null>(null);
  const [cursor, setCursor] = useState<string>('default');
  const [renderFault, setRenderFault] = useState<string | null>(null);

  const draw = useCallback(() => {
    frameRef.current = null;
    const canvas = canvasRef.current;
    const host = hostRef.current;
    if (!canvas || !host) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setRenderFault('This browser did not provide a 2D drawing context for the canvas.');
      return;
    }
    const { width, height, pixelRatio } = sizeRef.current;
    if (width <= 1 || height <= 1) {
      setRenderFault('The canvas has no size, so nothing can be drawn on it.');
      return;
    }
    setRenderFault(null);

    // The grid is chrome, not content: it is painted as an underlay here and
    // never reaches an export.
    const gridColor = cssVar(host, '--canvas-grid', '#eef0f4');
    const drawGrid = (target: CanvasRenderingContext2D) => {
      if (view.zoom <= 0.45) return;
      const step = GRID_SPACING * view.zoom;
      target.fillStyle = gridColor;
      const offsetX = -((view.scrollX * view.zoom) % step);
      const offsetY = -((view.scrollY * view.zoom) % step);
      const size = view.zoom > 1.6 ? 2 : 1;
      for (let x = offsetX; x < width; x += step) {
        for (let y = offsetY; y < height; y += step) {
          target.fillRect(Math.round(x), Math.round(y), size, size);
        }
      }
    };

    renderScene(
      ctx,
      elements,
      {
        width,
        height,
        pixelRatio,
        zoom: view.zoom,
        scrollX: view.scrollX,
        scrollY: view.scrollY,
        background: cssVar(host, '--canvas', '#ffffff'),
      },
      measure,
      drawGrid,
    );

    const selected = elements.filter((el) => selectedIds.includes(el.id) && !el.isDeleted);
    const bindTarget = hoverBindTargetId
      ? elements.find((el) => el.id === hoverBindTargetId) ?? null
      : null;
    renderOverlay(ctx, {
      view,
      pixelRatio,
      selected,
      marquee,
      bindTarget,
      peers,
      colors: {
        selection: cssVar(host, '--selection', '#1554c0'),
        surface: cssVar(host, '--surface', '#ffffff'),
        accentSoft: cssVar(host, '--accent-soft', '#e5edfb'),
      },
    });
  }, [elements, selectedIds, view, marquee, hoverBindTargetId, peers, measure]);

  const scheduleDraw = useCallback(() => {
    if (frameRef.current !== null) return;
    frameRef.current = requestAnimationFrame(draw);
  }, [draw]);

  // Kept in a ref so the sizing effect below mounts once instead of tearing down
  // and re-running on every repaint.
  const scheduleDrawRef = useRef(scheduleDraw);
  scheduleDrawRef.current = scheduleDraw;

  // Size the backing store to the host box times the device pixel ratio.
  useLayoutEffect(() => {
    const host = hostRef.current;
    const canvas = canvasRef.current;
    if (!host || !canvas) return undefined;

    const apply = () => {
      const rect = host.getBoundingClientRect();
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 3);
      const width = Math.max(1, Math.floor(rect.width));
      const height = Math.max(1, Math.floor(rect.height));
      const previous = sizeRef.current;
      // Bail out when nothing actually changed. Without this the observer can
      // report the same box repeatedly and each report costs a React update and
      // a full canvas reset, which is how this turns into an update loop.
      if (
        previous.width === width &&
        previous.height === height &&
        previous.pixelRatio === pixelRatio
      ) {
        return;
      }
      sizeRef.current = { width, height, pixelRatio };
      canvas.width = Math.floor(width * pixelRatio);
      canvas.height = Math.floor(height * pixelRatio);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      onViewportChange({ width, height });
      scheduleDrawRef.current();
    };

    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(host);
    window.addEventListener('resize', apply);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', apply);
    };
  }, [onViewportChange]);

  useEffect(() => {
    scheduleDraw();
  }, [scheduleDraw]);

  // Latest view/dispatch for the native wheel listener, so the listener itself
  // is attached once and never has to be torn down mid-gesture.
  const wheelState = useRef({ view, dispatch });
  wheelState.current = { view, dispatch };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const onWheel = (event: WheelEvent) => {
      // Registered with passive: false precisely so this works.
      event.preventDefault();
      const { view: current, dispatch: send } = wheelState.current;
      const rect = canvas.getBoundingClientRect();
      const anchor = { x: event.clientX - rect.left, y: event.clientY - rect.top };
      if (event.ctrlKey || event.metaKey) {
        // Trackpad pinch arrives as ctrl+wheel with small deltas; a mouse wheel
        // sends large ones. Scaling by the delta keeps both feeling right.
        const factor = Math.exp(-event.deltaY * 0.01);
        send({ t: 'zoomBy', factor: Math.min(1.4, Math.max(0.71, factor)), anchor });
        return;
      }
      send({
        t: 'setView',
        view: {
          ...current,
          scrollX: current.scrollX + event.deltaX / current.zoom,
          scrollY: current.scrollY + event.deltaY / current.zoom,
        },
      });
    };
    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', onWheel);
  }, []);

  useEffect(
    () => () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        // Clearing the id is essential, not tidiness. StrictMode mounts this
        // component twice in development: if the cancelled id is left in place,
        // every later scheduleDraw sees "a frame is already pending" and returns,
        // so the canvas never paints again and the board looks permanently blank.
        frameRef.current = null;
      }
    },
    [],
  );

  const finishFromWindow = useRef<((event: PointerEvent) => void) | null>(null);

  const toWorld = useCallback(
    (event: { clientX: number; clientY: number }): Vec => {
      const canvas = canvasRef.current;
      if (!canvas) return { x: 0, y: 0 };
      const rect = canvas.getBoundingClientRect();
      return screenToWorld(view, { x: event.clientX - rect.left, y: event.clientY - rect.top });
    },
    [view],
  );

  useEffect(() => {
    if (!interacting) return undefined;
    const finish = (event: PointerEvent) => {
      localPanRef.current = null;
      // A second dispatch is harmless: the reducer ignores a pointerUp with no
      // interaction in flight.
      dispatch({ t: 'pointerUp', world: toWorld(event) });
    };
    const cancel = () => {
      localPanRef.current = null;
      dispatch({ t: 'pointerCancel' });
    };
    finishFromWindow.current = finish;
    window.addEventListener('pointerup', finish);
    window.addEventListener('pointercancel', cancel);
    window.addEventListener('blur', cancel);
    return () => {
      finishFromWindow.current = null;
      window.removeEventListener('pointerup', finish);
      window.removeEventListener('pointercancel', cancel);
      window.removeEventListener('blur', cancel);
    };
  }, [interacting, dispatch, toWorld]);

  const updateCursor = useCallback(
    (world: Vec) => {
      if (tool === 'pan' || panOverride) {
        setCursor('grab');
        return;
      }
      if (tool !== 'select') {
        setCursor(tool === 'eraser' ? 'cell' : 'crosshair');
        return;
      }
      if (selectedIds.length === 1) {
        const el = elements.find((e) => e.id === selectedIds[0]);
        if (el) {
          const handle = pickHandle(el, world, view.zoom);
          if (handle?.kind === 'resize') {
            setCursor(cursorForHandle(handle.handle, el.angle));
            return;
          }
          if (handle?.kind === 'rotate' || handle?.kind === 'point') {
            setCursor('grab');
            return;
          }
        }
      }
      const hit = pickElement(
        elements.filter((el) => !el.isDeleted),
        world,
        measure,
        view.zoom,
      );
      setCursor(hit ? 'move' : 'default');
    },
    [tool, panOverride, selectedIds, elements, view.zoom, measure],
  );

  return (
    <div className="canvas-host" ref={hostRef}>
      {renderFault ? (
        <div className="render-fault" role="alert">
          <strong>The board cannot be drawn.</strong>
          <span>{renderFault}</span>
          <span>
            Your boards are still safe in storage. Export JSON from the top bar to get them out.
          </span>
        </div>
      ) : null}
      <canvas
        ref={canvasRef}
        id="board-canvas"
        role="application"
        aria-label="Whiteboard canvas. Use the tool buttons to draw, arrow keys to nudge the selection, and the board contents panel to move through elements with the keyboard."
        tabIndex={0}
        style={{ cursor: cursor === 'grab' && interacting ? 'grabbing' : cursor }}
        onPointerDown={(event) => {
          const canvas = canvasRef.current;
          if (event.button !== 0 && event.button !== 1) return;
          // Pointer capture keeps a drag alive over the toolbar and off-window.
          // It is a nice-to-have, never a precondition: some browsers throw here,
          // and a throw must not stop the drag from starting.
          try {
            canvas?.setPointerCapture(event.pointerId);
          } catch {
            // Drag still works, it just stops tracking outside the canvas.
          }
          try {
            canvas?.focus();
          } catch {
            // Focus is only needed for keyboard shortcuts.
          }
          if (panOverride || event.button === 1) {
            event.preventDefault();
            localPanRef.current = {
              startClient: { x: event.clientX, y: event.clientY },
              startScroll: { x: view.scrollX, y: view.scrollY },
            };
            return;
          }
          dispatch({
            t: 'pointerDown',
            world: toWorld(event),
            shift: event.shiftKey,
            alt: event.altKey,
          });
        }}
        onPointerMove={(event) => {
          const pan = localPanRef.current;
          if (pan) {
            dispatch({
              t: 'setView',
              view: {
                ...view,
                scrollX: pan.startScroll.x - (event.clientX - pan.startClient.x) / view.zoom,
                scrollY: pan.startScroll.y - (event.clientY - pan.startClient.y) / view.zoom,
              },
            });
            return;
          }
          const world = toWorld(event);
          onPointerWorld(world);
          if (interacting) dispatch({ t: 'pointerMove', world, shift: event.shiftKey });
          else updateCursor(world);
        }}
        onPointerUp={(event) => {
          try {
            canvasRef.current?.releasePointerCapture(event.pointerId);
          } catch {
            // Firefox throws when capture was never taken. Releasing is optional;
            // finishing the gesture below is not.
          }
          if (localPanRef.current) {
            localPanRef.current = null;
            return;
          }
          dispatch({ t: 'pointerUp', world: toWorld(event) });
        }}
        onPointerCancel={() => {
          localPanRef.current = null;
          dispatch({ t: 'pointerCancel' });
        }}
        onDoubleClick={(event) => dispatch({ t: 'doubleClick', world: toWorld(event) })}
      />
    </div>
  );
}
